import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { useAccounts } from "../../api/hooks/useAccounts";
import { useAddLeg } from "../../api/hooks/useAddLeg";
import { useCreateTrade } from "../../api/hooks/useCreateTrade";
import { useStrategies } from "../../api/hooks/useStrategies";
import { InstrumentTypeahead } from "../instruments/InstrumentTypeahead";
import { Button } from "../ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import { Input } from "../ui/input";

const LAST_USED_ACCOUNT_KEY = "lastUsedAccountId";
const decimalRegex = /^\d+(\.\d+)?$/;

const addTradeSchema = z.object({
  account_id: z.string().uuid({ message: "Pick an account" }),
  currency: z
    .string()
    .length(3, { message: "Currency must be a 3-letter ISO code" })
    .regex(/^[A-Za-z]{3}$/, { message: "Currency must be 3 letters" })
    .transform((value) => value.toUpperCase()),
  notes: z.string().max(10_000, { message: "Notes must be 10,000 characters or fewer" }),
  strategy_id: z
    .string()
    .trim()
    .refine((value) => value === "" || z.string().uuid().safeParse(value).success, {
      message: "Pick a strategy or leave blank",
    }),
  planned_risk_amount: z
    .string()
    .trim()
    .refine((value) => value === "" || decimalRegex.test(value), {
      message: "Planned risk must be a positive decimal (e.g., 150.00)",
    }),
  instrument_id: z.string().uuid({ message: "Pick a symbol" }),
  direction: z.enum(["long", "short"]),
  target_qty: z
    .string()
    .trim()
    .refine((value) => decimalRegex.test(value), {
      message: "Qty must be a positive decimal",
    }),
});

type AddTradeFormValues = z.infer<typeof addTradeSchema>;

const defaultValues: AddTradeFormValues = {
  account_id: "",
  currency: "USD",
  notes: "",
  strategy_id: "",
  planned_risk_amount: "",
  instrument_id: "",
  direction: "long",
  target_qty: "",
};

function readLastUsedAccount(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(LAST_USED_ACCOUNT_KEY);
  } catch {
    return null;
  }
}

function writeLastUsedAccount(id: string): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(LAST_USED_ACCOUNT_KEY, id);
  } catch {
    // ignore
  }
}

export type AddTradeFormProps = {
  onCreated?: (tradeId: string) => void;
  onCancel?: () => void;
};

export function AddTradeForm({ onCreated, onCancel }: AddTradeFormProps) {
  const form = useForm<AddTradeFormValues>({
    resolver: zodResolver(addTradeSchema),
    defaultValues,
  });
  const createTrade = useCreateTrade();
  const addLeg = useAddLeg();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { data: accountsData, isLoading: accountsLoading } = useAccounts();
  const accounts = (accountsData?.items ?? []).filter((a) => !a.is_deleted);
  const hideAccountField = accounts.length === 1;

  const { data: strategiesData } = useStrategies();
  const strategies = (strategiesData?.items ?? []).filter((s) => !s.is_deleted);

  const currentAccountId = form.watch("account_id");

  useEffect(() => {
    if (accountsLoading || accounts.length === 0) return;
    if (currentAccountId !== "" && accounts.some((a) => a.account_id === currentAccountId)) {
      return;
    }
    const lastUsed = readLastUsedAccount();
    const first = accounts[0];
    if (first === undefined) return;
    const pick =
      lastUsed !== null && accounts.some((a) => a.account_id === lastUsed)
        ? lastUsed
        : first.account_id;
    form.setValue("account_id", pick, { shouldValidate: false });
  }, [accountsLoading, accounts, currentAccountId, form]);

  async function onSubmit(values: AddTradeFormValues) {
    setSubmitError(null);
    try {
      const trade = await createTrade.mutateAsync({
        account_id: values.account_id,
        currency: values.currency,
        notes: values.notes,
        strategy_id: values.strategy_id === "" ? null : values.strategy_id,
        planned_risk_amount:
          values.planned_risk_amount === "" ? null : values.planned_risk_amount,
      });
      await addLeg.mutateAsync({
        tradeId: trade.trade_id,
        body: {
          instrument_id: values.instrument_id,
          direction: values.direction,
          target_qty: values.target_qty,
        },
      });
      writeLastUsedAccount(values.account_id);
      onCreated?.(trade.trade_id);
    } catch (err) {
      setSubmitError(
        err instanceof Error ? err.message : "Failed to create trade. Try again.",
      );
    }
  }

  const isSubmitting = createTrade.isPending || addLeg.isPending;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5" noValidate>
        {!hideAccountField && (
          <FormField
            control={form.control}
            name="account_id"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Account</FormLabel>
                <FormControl>
                  <select
                    className="flex h-9 w-full rounded-md border border-outline bg-surface-container px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-focus-ring disabled:cursor-not-allowed disabled:opacity-50"
                    {...field}
                  >
                    <option value="" disabled>
                      Choose an account…
                    </option>
                    {accounts.map((a) => (
                      <option key={a.account_id} value={a.account_id}>
                        {a.name}
                      </option>
                    ))}
                  </select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
          <FormField
            control={form.control}
            name="direction"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Side</FormLabel>
                <FormControl>
                  <select
                    className="flex h-9 w-full rounded-md border border-outline bg-surface-container px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-focus-ring"
                    {...field}
                  >
                    <option value="long">LONG</option>
                    <option value="short">SHORT</option>
                  </select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="target_qty"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Qty</FormLabel>
                <FormControl>
                  <Input
                    inputMode="decimal"
                    placeholder="100"
                    autoComplete="off"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="instrument_id"
            render={({ field }) => (
              <FormItem className="sm:col-span-2">
                <FormLabel>Symbol</FormLabel>
                <FormControl>
                  <InstrumentTypeahead
                    value={field.value}
                    onChange={(id) => {
                      field.onChange(id);
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="strategy_id"
            render={({ field }) => (
              <FormItem className="sm:col-span-2">
                <FormLabel>Strategy</FormLabel>
                <FormControl>
                  <select
                    className="flex h-9 w-full rounded-md border border-outline bg-surface-container px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-focus-ring"
                    {...field}
                  >
                    <option value="">No strategy</option>
                    {strategies.map((s) => (
                      <option key={s.strategy_id} value={s.strategy_id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="currency"
            render={({ field }) => (
              // TODO(phase-1b): derive from account.baseCurrency
              <FormItem className="sm:col-span-2">
                <FormLabel>Currency</FormLabel>
                <FormControl>
                  <Input
                    maxLength={3}
                    placeholder="USD"
                    autoComplete="off"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <details className="rounded-md border border-outline-variant bg-surface-container-low">
          <summary className="cursor-pointer select-none px-3 py-2 text-sm font-medium text-on-surface">
            More details
          </summary>
          <div className="space-y-4 px-3 pb-3 pt-1">
            <FormField
              control={form.control}
              name="planned_risk_amount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Planned risk</FormLabel>
                  <FormControl>
                    <Input
                      inputMode="decimal"
                      placeholder="150.00"
                      autoComplete="off"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    Positive decimal in the trade&apos;s currency. Leave blank to skip.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes</FormLabel>
                  <FormControl>
                    <textarea
                      className="flex min-h-[80px] w-full rounded-md border border-outline bg-surface-container px-3 py-2 text-sm shadow-sm placeholder:text-on-surface-muted focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-focus-ring disabled:cursor-not-allowed disabled:opacity-50"
                      rows={3}
                      maxLength={10_000}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </details>

        {submitError !== null && (
          <p role="alert" className="text-sm font-medium text-danger">
            {submitError}
          </p>
        )}

        <div className="flex items-center justify-end gap-3">
          {onCancel && (
            <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
              Cancel
            </Button>
          )}
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Creating…" : "Create trade"}
          </Button>
        </div>
      </form>
    </Form>
  );
}
